import axios from 'axios';
import { queryClient } from './queryClient';

const cache = new Map();
const inflight = new Map();
const CACHE_TTL = 30000;

const api = axios.create({
  baseURL: '/api',
  timeout: 30000,
  withCredentials: true,
});

// Global loading signal — a slim top progress bar subscribes to this so the
// whole app shows activity whenever any request is in flight.
let _pending = 0;
const _notifyLoading = () => {
  try { window.dispatchEvent(new CustomEvent('api-loading', { detail: _pending })); } catch { /* SSR/no-window */ }
};
api.interceptors.request.use((c) => { _pending++; _notifyLoading(); return c; }, (e) => { _pending = Math.max(0, _pending - 1); _notifyLoading(); return Promise.reject(e); });
api.interceptors.response.use((r) => { _pending = Math.max(0, _pending - 1); _notifyLoading(); return r; }, (e) => { _pending = Math.max(0, _pending - 1); _notifyLoading(); return Promise.reject(e); });

api.interceptors.request.use((config) => {
  // Granular cache invalidation on mutations — only clear in-memory Map cache
  // Components handle their own TanStack Query refreshes after mutations
  if (['post', 'put', 'patch', 'delete'].includes(config.method)) {
    const url = config.url || '';
    // Skip cache invalidation for read-like POST endpoints
    const readOnlyPosts = ['batch-traits', 'check-duplicate', 'check-ats', 'bulk-resume-upload', 'company-kb/batch-lookup'];
    const isReadPost = config.method === 'post' && readOnlyPosts.some(p => url.includes(p));
    if (!isReadPost) {
      const segment = url.replace(/^\//, '').split('/')[0];
      if (segment) {
        api.clearCache(segment);
      } else {
        cache.clear();
      }
    }
  }
  return config;
});

// Multi-tenant: attach the active workspace as a SELECTOR only. The server
// always validates membership + derives the role from the DB (never the header).
api.interceptors.request.use((config) => {
  try {
    const ws = localStorage.getItem('hj_active_workspace');
    if (ws) config.headers['X-Workspace-Id'] = ws;
  } catch { /* no-op */ }
  return config;
});

// A cold pod serves the frontend from the CDN while its API is still loading,
// so every call 503s and the app looks dead rather than starting. Production
// RCA, Jun 2026: the owner met exactly that on a fresh pod whose routes took
// 110s to register. A 503 from the loading guard is now retried a few times
// with a short backoff, which is all a cold start needs.
const STARTING_RETRIES = 4;
api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const cfg = err.config;
    const starting = err.response?.status === 503
      && /starting up/i.test(err.response?.data?.detail || '');
    if (starting && cfg && (cfg._startingRetries || 0) < STARTING_RETRIES) {
      cfg._startingRetries = (cfg._startingRetries || 0) + 1;
      const wait = Number(err.response.headers?.['retry-after'] || 2) * 1000;
      window.dispatchEvent(new CustomEvent('hj:api-starting',
        { detail: { attempt: cfg._startingRetries } }));
      await new Promise(r => setTimeout(r, wait * cfg._startingRetries));
      return api(cfg);
    }
    return Promise.reject(err);
  }
);

api.interceptors.response.use(
  (res) => res,
  (err) => {
    // Normalise object-typed FastAPI detail payloads (e.g. the 409 forced-
    // duplicate response) to their `.message` string so the dozens of
    // ``toast.error(err.response.data.detail || 'fallback')`` call sites
    // throughout the app don't try to render an object as a React child
    // (React error #31). Components that need the structured fields (force
    // submit dialog, etc.) read err.response.data.detail_object instead.
    //
    // iter_345 fix: also handle ARRAY detail (Pydantic 422 validation
    // errors are shaped `[{type, loc, msg, input, url}, ...]`). Previously
    // arrays fell through and blew up any component that rendered
    // `err.response.data.detail` as a React child (crashed the public
    // job-application page — the "Something went wrong" screen users hit
    // via /jobs?apply=... on the marketing site).
    try {
      const d = err?.response?.data?.detail;
      if (Array.isArray(d)) {
        err.response.data.detail_object = d;
        err.response.data.detail = d
          .map((e) => {
            if (!e) return '';
            if (typeof e === 'string') return e;
            const loc = Array.isArray(e.loc) ? e.loc.filter(Boolean).slice(-1)[0] : e.loc;
            const field = loc ? `${loc}: ` : '';
            return field + (e.msg || e.message || 'Invalid');
          })
          .filter(Boolean)
          .join(' · ') || 'Validation failed';
      } else if (d && typeof d === 'object') {
        err.response.data.detail_object = d;
        err.response.data.detail =
          d.message ||
          d.msg ||
          d.error ||
          (d.detail && typeof d.detail === 'string' ? d.detail : null) ||
          'Request failed';
      }
    } catch (e) {
      // swallow — never let normalisation itself break error handling
      void e;
    }

    if (err.response?.status === 401) {
      const detail = err.response?.data?.detail;
      const url = err.config?.url || '';
      const isAuthEndpoint = url.includes('/auth/login') ||
        url.includes('/auth/verify-otp') ||
        url.includes('/auth/google/') ||
        url.includes('/auth/me') ||
        url.includes('/auth/exchange-token');
      // Only redirect to login when on protected /app/* pages, not public marketing pages
      const isProtectedPage = window.location.pathname.startsWith('/app');
      if (isProtectedPage && !isAuthEndpoint) {
        const REASONS = ['session_expired', 'session_replaced', 'account_deactivated', 'account_locked', 'signed_out_by_admin', 'password_changed'];
        if (REASONS.includes(detail)) {
          window.location.href = `/teamlogin?reason=${detail}`;
        } else {
          window.location.href = '/teamlogin';
        }
      }
    }
    return Promise.reject(err);
  }
);

// Cached GET — now backed by TanStack Query for deduplication + stale-while-revalidate
api.getCached = async (url, ttl = CACHE_TTL) => {
  try {
    return await queryClient.fetchQuery({
      queryKey: ['api', url],
      queryFn: () => api.get(url),
      staleTime: ttl,
      gcTime: ttl * 5,
    });
  } catch (err) {
    // Stale-while-revalidate: return stale data from old Map cache on error
    const cached = cache.get(url);
    if (cached) return cached.res;
    throw err;
  }
};

api.clearCache = (pattern) => {
  // Clear old Map cache
  if (!pattern) { cache.clear(); return; }
  for (const key of cache.keys()) {
    if (key.includes(pattern)) cache.delete(key);
  }
};

export default api;
